import React from 'react';
import { Button, Section, SectionHeader } from '../../design-system';
import MarketingLayout from '../layouts/MarketingLayout';
import FadeIn from '../components/FadeIn';
import SeoHead from '../seo/SeoHead';
import { FOR_BUSINESS_SEO } from '../seo/pages';
import { INSTALLATION_SHOTS } from '../../assets/installationShots';
import screenImage from '../../assets/images/cover3.png';

const STEPS = [
  {
    step: '01',
    title: 'Install INVERT',
    description:
      'Plug in the restaurant TV box or install the app on your existing TVs. No expensive digital signage software required.',
  },
  {
    step: '02',
    title: 'Just type',
    description:
      'Tell INVERT what restaurant TV content you want — menus, specials, hiring. AI generates beautiful, branded screens instantly.',
  },
  {
    step: '03',
    title: 'Every screen updates',
    description:
      'One prompt updates every display in your restaurant or venue — perfectly formatted, automatically scheduled.',
  },
  {
    step: '04',
    title: 'Earn while idle',
    description:
      "When you're not promoting, INVERT fills unused time with local ads. Passive income, built in.",
  },
];

export default function ForBusiness() {
  return (
    <MarketingLayout>
      <SeoHead
        title={FOR_BUSINESS_SEO.title}
        description={FOR_BUSINESS_SEO.description}
        path={FOR_BUSINESS_SEO.path}
      />

      <section className="bg-invert-navy text-white pt-32 lg:pt-40 pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <p className="text-xs font-semibold uppercase tracking-widest text-invert-orange mb-6">
                For Business
              </p>
              <h1 className="font-interblack text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-6">
                Restaurant TV box. Type what you want. Every screen updates instantly.
              </h1>
              <p className="text-xl text-slate-300 mb-8">
                You already own TVs. Instead of buying digital signage software, hiring designers, or creating PowerPoints —
                install INVERT and publish restaurant TV content by typing.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button to="/screenHostSignup" size="lg">Get Started</Button>
                <Button to="/restaurant-tv" variant="secondary" size="lg">
                  Restaurant TV Guide
                </Button>
              </div>
              <p className="text-sm text-slate-400 mt-6">
                <Button to="/proof" variant="ghost" size="sm" className="!p-0 !h-auto text-invert-orange hover:text-invert-orange-deep">
                  See who&apos;s already on the network →
                </Button>
              </p>
            </FadeIn>
            <FadeIn delay={0.15}>
              <img
                src={screenImage}
                alt="INVERT restaurant TV content on a business display"
                className="rounded-2xl shadow-2xl"
              />
            </FadeIn>
          </div>
        </div>
      </section>

      <Section>
        <SectionHeader
          label="How It Works"
          title="From restaurant TV box to live content in minutes"
          subtitle="No designers. No complicated software. Just type."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {STEPS.map((item, i) => (
            <FadeIn key={item.step} delay={i * 0.1}>
              <div>
                <span className="text-4xl font-bold text-invert-orange/30">{item.step}</span>
                <h3 className="text-lg font-semibold text-invert-navy mt-2 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section className="bg-slate-50">
        <SectionHeader
          label="Hardware Setup"
          title="Restaurant TV box: plug in and start playing"
          subtitle="Get the INVERT restaurant TV box, connect it to your TV, and your screens are live."
        />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {INSTALLATION_SHOTS.map((shot, i) => (
            <FadeIn key={shot.alt} delay={i * 0.08}>
              <div className="rounded-xl overflow-hidden aspect-[4/3]">
                <img src={shot.src} alt={shot.alt} className="w-full h-full object-cover" />
              </div>
            </FadeIn>
          ))}
        </div>
        <FadeIn className="text-center">
          <Button to="/contactUs?intent=host" size="lg">
            Request Installation
          </Button>
        </FadeIn>
      </Section>

      <Section dark className="text-center">
        <FadeIn className="max-w-2xl mx-auto">
          <h2 className="font-interblack text-3xl lg:text-4xl text-white mb-6">
            Every restaurant TV becomes an AI employee
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Join restaurants transforming how they show menus and specials — and earning passive income from unused screen time.
          </p>
          <Button to="/screenHostSignup" size="lg">Create Account</Button>
        </FadeIn>
      </Section>
    </MarketingLayout>
  );
}
